import { emojiDefaults } from "@/components/emoji/emoji.type.ts";
import { EmojiPickerDropdown } from "@/components/emoji/EmojiPickerDropdown.tsx";
import i18n from "@/i18n.ts";
import { ScoreCardEditSidebar } from "@/pages/recruit/scorecards/edit/components/ScoreCardEditSidebar.tsx";
import {
  ScoreCardsEditPageStep,
  useScoreCardEditPage,
} from "@/pages/recruit/scorecards/edit/components/scorecards-edit-pages.tsx";
import { ScoreCardEditPageProps } from "@/pages/recruit/scorecards/edit/scorecards-edit.ts";
import { useScoreCardSave } from "@/queries/recruit/use-scorecards.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Avatar,
  Box,
  Button,
  FontAwesomeIcon,
  FullScreenPage,
  Input,
  Stack,
  Textarea,
} from "@skailer-com/uikit";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export function ScoreCardEditDetailsPage({ scoreCard }: ScoreCardEditPageProps) {
  const { t } = useTranslation();
  const { navigateToStep, confirmExit } = useScoreCardEditPage();

  const ScoreCardsEditDetailsSchemaObject = z.object({
    title: z
      .string({ error: i18n.t("errors:field_required") })
      .min(1, i18n.t("errors:field_required")),
    emoji: z.string().nullable(),
    description: z.string().nullable(),
  });

  type ScoreCardsEditDetailsSchema = z.infer<typeof ScoreCardsEditDetailsSchemaObject>;

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ScoreCardsEditDetailsSchema>({
    mode: "onSubmit",
    resolver: zodResolver(ScoreCardsEditDetailsSchemaObject),
    values: {
      title: scoreCard.title,
      emoji: scoreCard.emoji ?? emojiDefaults.scoreCards.default,
      description: scoreCard.description,
    },
  });

  const scoreCardUpdate = useScoreCardSave(control);

  const onSubmit = handleSubmit(async (data) => {
    try {
      await scoreCardUpdate.mutateAsync({
        id: scoreCard.id,
        payload: {
          title: data.title,
          description: data.description ?? "",
          emoji: data.emoji ?? "",
        },
      });

      analyticsService.trackEvent(analyticEvents.scoreCards.edited, {
        [analyticProperties.id]: scoreCard.id,
        [analyticProperties.tab]: ScoreCardsEditPageStep.details,
      });

      navigateToStep(scoreCard.id, ScoreCardsEditPageStep.questions);
    } catch {
      //
    }
  });

  return (
    <FullScreenPage
      title={t("scorecards:scorecard")}
      isCenterVertical={false}
      backSlot={
        <Button variant={"secondary"} size={"lg"} onClick={confirmExit}>
          {t("common:exit")}
        </Button>
      }
      actionsSlot={
        <Button
          type={"submit"}
          size={"lg"}
          isLoading={scoreCardUpdate.isPending}
          onClick={onSubmit}
        >
          {t("common:continue")}
        </Button>
      }
      sidebarSlot={
        <ScoreCardEditSidebar step={ScoreCardsEditPageStep.details} scoreCard={scoreCard} />
      }
    >
      <form onSubmit={onSubmit}>
        <Stack gap={"sm"} justify={"center"}>
          <Stack className={"card-panel"} gap={"lg"}>
            <Stack>
              <Controller
                control={control}
                name={"emoji"}
                render={({ field: { value, onChange } }) => (
                  <EmojiPickerDropdown
                    onEmojiSelect={(em) => {
                      onChange(em);
                    }}
                  >
                    <Box
                      className={
                        "relative w-41 h-41 group cursor-pointer rounded-md bg-brand-dark/5 shadow-lg"
                      }
                    >
                      {value && (
                        <Avatar variant={"plain"} url={undefined} emoji={value} size={"2xl"} />
                      )}
                      <Box
                        className={
                          "bg-dark/20 opacity-0 group-hover:opacity-100 absolute inset-0 rounded-md flex items-center justify-center"
                        }
                      >
                        <FontAwesomeIcon
                          icon={"fa-light fa-icons"}
                          className={"text-[74px] text-light "}
                        />
                      </Box>
                    </Box>
                  </EmojiPickerDropdown>
                )}
              />

              {errors.emoji && (
                <Box className="pt-2 pb-4">
                  <span className="text-danger">{errors.emoji.message}</span>
                </Box>
              )}
            </Stack>
            <Input
              type={"text"}
              label={t("common:title")}
              size={"lg"}
              required={true}
              {...register("title")}
              error={errors.title?.message}
            />
            <Textarea
              label={t("common:description")}
              {...register("description")}
              error={errors.description?.message}
            />
          </Stack>

          <Button type={"submit"} size={"lg"} isLoading={scoreCardUpdate.isPending}>
            {t("common:continue")}
          </Button>
        </Stack>
      </form>
    </FullScreenPage>
  );
}
